/**
 * Inventory Forecasting Tool - Type Definitions
 *
 * This file contains all TypeScript types, interfaces, and constants used across
 * the Inventory page components. Central location for type management to ensure
 * consistency and easy updates.
 *
 * Key concepts:
 * - BucketGranularity: Time period grouping for data display (day/week/month/quarter)
 * - SalesModifiers: Multipliers applied to projected sales (global or per-inventory)
 * - IncomingPOs: Purchase orders that affect inventory projections
 */

import {
  IncomingPO,
  POContribution,
  PurchaseOrder,
} from "models/InventoryModel";

// ============================================================================
// Display & UI Types
// ============================================================================

/**
 * Time period granularity for aggregating inventory data in the table.
 * Controls how date columns are grouped and displayed.
 */
export type BucketGranularity = "day" | "week" | "month" | "quarter";

/**
 * Options for the bucket granularity dropdown
 */
export const BUCKET_OPTIONS: Array<{ value: BucketGranularity; label: string }> = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

/**
 * Options for items per page dropdown
 */
export const GROUPINGS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

// ============================================================================
// Sales Modifiers Types
// ============================================================================

/**
 * Sales modifiers are decimal multipliers applied to projected sales.
 * Key is month in "YYYY-MM" format, value is multiplier (e.g., 1.2 = 120%, 0.5 = 50%)
 *
 * Example: { "2024-01": 1.2, "2024-02": 0.8 }
 * This would increase January projections by 20% and decrease February by 20%
 */
export type MonthlyModifiers = Record<string, number>;

/**
 * Per-inventory modifiers keyed by inventory_id (not the composite key).
 * These override global modifiers for specific inventory items.
 */
export type InventoryModifiers = Record<string, MonthlyModifiers>;

/**
 * Complete sales modifiers data structure.
 * - global: Applies to all inventory items
 * - byInventory: Overrides for specific items (takes precedence over global)
 */
export type SalesModifiersData = {
  global: MonthlyModifiers;
  byInventory: InventoryModifiers;
};

// ============================================================================
// Purchase Order Types
// ============================================================================

/**
 * Incoming POs keyed by inventory_id.
 * Each inventory can have multiple incoming POs with different arrive dates.
 */
export type IncomingPOsByInventory = Record<string, IncomingPO[]>;

// ============================================================================
// Run Rate Types
// ============================================================================

/**
 * Mode for calculating run rate basis.
 * - rolling: Uses last X days of sales data
 * - fixed: Uses a specific date range for calculation
 */
export type RunRateBasisMode = "rolling" | "fixed";

// ============================================================================
// SKU Modal Types
// ============================================================================

/**
 * Data for the SKU list modal popup.
 * Shown when inventory has more than 3 SKUs.
 */
export type SkuModalData = {
  skus: string[];
  inventoryName: string;
} | null;

// ============================================================================
// Period Column Types
// ============================================================================

/**
 * Represents a time period column in the inventory table.
 * Used for displaying aggregated data across date ranges.
 */
export type PeriodColumn = {
  key: string;
  label: string;
  periodStart: string;
  periodEnd: string;
};

/**
 * Metric values computed for each inventory grouping per time period.
 * These are the core data points displayed in each table cell.
 */
export type PeriodMetrics = {
  startingInventory: number | null;
  soldQuantity: number | null;
  inventoryAdded: number | null;
  confirmedInventoryAdded: number | null;
  draftInventoryAdded: number | null;
  miscAdjustment: number | null;
  endingInventory: number | null;
  currentStock: number | null;
  inventoryValue: number | null;
  /** Breakdown tooltip for forecasted sold quantity */
  soldBreakdown: string | null;
  /** Breakdown tooltip for ending inventory */
  endingBreakdown: string | null;
  /** POs behind the period's Placed / Incoming total */
  confirmedPOBreakdown: POContribution[] | null;
  /** POs behind the period's Draft / Proposed total */
  draftPOBreakdown: POContribution[] | null;
  /** Units ShipBob stowed from linked WROs in the period (part of Misc Adjustment) */
  stowedPOBreakdown: POContribution[] | null;
};

// ============================================================================
// Re-exports from InventoryModel for convenience
// ============================================================================

// ============================================================================
// Manual Override Types
// ============================================================================

/**
 * Manual overrides for projected sales by inventory and period.
 * Structure: { [inventoryId]: { [periodKey]: overrideValue } }
 * periodKey format: "YYYY-MM" for months, "YYYY-MM-DD" for days
 */
export type ManualOverrides = Record<string, Record<string, number>>;

// ============================================================================
// Projection Breakdown Types
// ============================================================================

/**
 * Projection breakdown data for the detail dialog.
 * Contains calculated values explaining how projections are determined.
 */
export type ProjectionBreakdown = {
  dateRangeStart: string;
  dateRangeEnd: string;
  totalSales: number;
  daysInRange: number;
  dailyAverage: number;
  explanation: string; // Human-readable explanation
};

// ============================================================================
// Re-exports from InventoryModel for convenience
// ============================================================================

export type { IncomingPO, PurchaseOrder };

// ============================================================================
// Risk Summary Types
// ============================================================================

/**
 * Risk severity levels for inventory items
 * CRITICAL: Stock-out imminent (< 1 month)
 * AT-RISK: Stock-out approaching (1-2 months)
 * WATCH: Stock-out possible (2-3 months)
 * OK: No immediate concern (> 3 months or no stock-out)
 */
export type RiskSeverity = 'CRITICAL' | 'AT-RISK' | 'WATCH' | 'OK';

/**
 * Timeframe options for risk assessment
 */
export type RiskTimeframe = number;

/**
 * Configuration for risk summary calculations
 */
export type RiskSummaryConfig = {
  timeframeMonths: RiskTimeframe;
  bufferMonths: number;                  // Default: 1
  defaultLeadTimeMonths: number;         // Default: 3.5
};

/**
 * At-risk inventory item with calculated projections
 */
export type InventoryRiskItem = {
  inventory_id: string;
  inventory_name: string | null;
  country: string;
  skus?: string[];                       // SKU IDs for filtering

  // Current state
  currentInventory: number;           // Today's ending inventory
  dailyRunRate: number;               // Average daily sales

  // Availability-adjusted demand over the server's trailing 120-day window.
  // Optional: absent until the API deploy that computes them lands.
  demandUnitsSold?: number;           // Units sold on in-stock days
  demandInStockDays?: number;         // Days with inventory on hand
  demandDailyRate?: number;           // demandUnitsSold / demandInStockDays

  // Projections
  projectedStockOutDate: string | null;  // YYYY-MM-DD or null if no stock-out
  daysUntilStockOut: number | null;      // Days from today

  // Recommendations
  leadTimeMonths: number;
  leadTimeSource?: LeadTimeSource;       // Where the lead time came from
  leadTimeCountryAdjustmentMonths?: number; // Included geography adjustment (0 = none)
  bufferMonths: number;
  recommendedOrderDate: string | null;   // YYYY-MM-DD
  daysUntilOrderNeeded: number | null;   // Can be negative (overdue)

  // Ranking
  projectedSalesInPeriod: number;        // Total projected sales in timeframe
  severity: RiskSeverity;

  // Reference
  groupingKey: string;                   // For linking to table row
};

/**
 * Which level a resolved lead time came from:
 * a per-SKU override, its manufacturer's default, or the global default.
 */
export type LeadTimeSource = "inventory" | "manufacturer" | "default";
